import { useEffect, useState } from 'react';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { UNLEASH_FETCH_TIMEOUTS_MS, unleashEnabled } from './feature-flags';
import { FLAG_RETRY_DELAYS_MS } from './UnleashContextSync';

/**
 * How long a FAILED first flag load is given to recover before a caller may
 * treat it as final: UnleashContextSync's retries (2 + 4 + 8 s) plus the
 * longest request limit (16 s).
 */
export const FLAG_RECOVERY_WINDOW_MS =
  FLAG_RETRY_DELAYS_MS.reduce((a, b) => a + b, 0) + UNLEASH_FETCH_TIMEOUTS_MS[UNLEASH_FETCH_TIMEOUTS_MS.length - 1];

/**
 * True once flags can be acted on as an answer: loaded, OR the load failed and
 * the recovery window has passed, OR there is no Unleash in this build (the
 * provider never mounts and flagsReady/flagsError are pinned forever).
 *
 * For decisions that cannot be undone on screen, such as a <Navigate replace>
 * away from a page. With the 8 s flag-request limit a slow first fetch
 * reports flagsError BEFORE the retries run, so treating flagsError as final
 * would redirect a user whose flags are about to arrive (review
 * a0fd6d09b5b575dd5).
 */
export function useFlagsSettled(): boolean {
  const { flagsReady, flagsError } = useFlagsStatus();
  const [gaveUp, setGaveUp] = useState(false);
  const failing = !flagsReady && !!flagsError;
  useEffect(() => {
    if (!failing) return;
    const t = setTimeout(() => setGaveUp(true), FLAG_RECOVERY_WINDOW_MS);
    return () => clearTimeout(t);
  }, [failing]);
  return !unleashEnabled || flagsReady || (failing && gaveUp);
}
